/**
 * EpisodeStatsChip.jsx — live readability chip used on writer upload
 * screens (iter 68). Runs the same word-count + reading-time formulas
 * that readers see, so writers self-edit toward the right length
 * without publishing first.
 *
 * Shows:
 *   • Word count
 *   • Standard read time (250 wpm — matches TextSeriesReader display)
 *   • Flash-read time (300 wpm — matches FlashReadOverlay default)
 *   • Reading level (light Flesch-Kincaid approximation: Easy /
 *     Average / Advanced) using avg sentence length + avg word length
 *     as cheap proxies for syllables/sentence + characters/word.
 *
 * Pure-presentational; pass `text` and we recompute on every keystroke.
 */
import React, { useMemo } from 'react';
import { Clock, Zap, Target, FileText } from 'lucide-react';

const STANDARD_WPM = 250; // matches TextSeriesReader formula
const FLASH_WPM = 300;    // matches FlashReadOverlay default

const countWords = (s) => (s ? s.trim().split(/\s+/).filter(Boolean).length : 0);

const countSentences = (s) => {
  if (!s) return 0;
  const matches = s.match(/[.!?]+\s|[.!?]+$/g);
  return Math.max(1, (matches || []).length);
};

const avgWordLen = (text) => {
  if (!text) return 0;
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return 0;
  const chars = words.reduce((a, w) => a + w.replace(/[^A-Za-z0-9]/g, '').length, 0);
  return chars / words.length;
};

// Bucket the reading level into 3 tiers — Easy (<= grade 6), Average
// (7-10), Advanced (11+). The thresholds are deliberately gentle: we
// want this to nudge writers, not gatekeep them.
const readingLevel = (wordCount, sentenceCount, avgChars) => {
  if (wordCount < 30) return null; // too short to score
  const wordsPerSentence = wordCount / sentenceCount;
  // Quick proxy: longer sentences + longer words → higher level.
  // Numbers chosen to map "child writes" → Easy, "adult novel" →
  // Average, "academic" → Advanced. Not a substitute for a real F-K
  // score, just directional.
  const score = (wordsPerSentence * 0.4) + (avgChars * 1.4);
  if (score <= 9) return { label: 'Easy', tint: 'text-emerald-300' };
  if (score <= 13) return { label: 'Average', tint: 'text-amber-300' };
  return { label: 'Advanced', tint: 'text-rose-300' };
};

const formatSeconds = (secs) => {
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s === 0 ? `${m} min` : `${m}m ${s}s`;
};

const EpisodeStatsChip = ({ text, className = '' }) => {
  const stats = useMemo(() => {
    const wc = countWords(text);
    if (!wc) return null;
    const sc = countSentences(text);
    const acl = avgWordLen(text);
    return {
      wordCount: wc,
      readMin: Math.max(1, Math.ceil(wc / STANDARD_WPM)),
      flashSecs: Math.max(1, Math.round((wc / FLASH_WPM) * 60)),
      level: readingLevel(wc, sc, acl),
    };
  }, [text]);

  if (!stats) {
    return (
      <p
        className={`text-[10px] text-white/35 italic ${className}`}
        data-testid="episode-stats-chip-empty"
      >
        Stats appear once you start typing…
      </p>
    );
  }

  return (
    <div
      className={`flex flex-wrap items-center gap-2 ${className}`}
      data-testid="episode-stats-chip"
    >
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-extrabold px-2 py-1 rounded-full bg-indigo-500/15 border border-indigo-400/30 text-indigo-200">
        <FileText className="w-3 h-3" />
        <span className="tabular-nums">{stats.wordCount.toLocaleString()}</span> words
      </span>
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-extrabold px-2 py-1 rounded-full bg-sky-500/15 border border-sky-400/30 text-sky-200">
        <Clock className="w-3 h-3" />
        ~{stats.readMin} min read
      </span>
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-extrabold px-2 py-1 rounded-full bg-amber-500/15 border border-amber-400/30 text-amber-200">
        <Zap className="w-3 h-3" />
        Flash {formatSeconds(stats.flashSecs)}
      </span>
      {stats.level && (
        <span
          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-extrabold px-2 py-1 rounded-full border bg-white/5 border-white/15 ${stats.level.tint}`}
          data-testid={`episode-stats-level-${stats.level.label.toLowerCase()}`}
        >
          <Target className="w-3 h-3" />
          {stats.level.label}
        </span>
      )}
    </div>
  );
};

export default EpisodeStatsChip;
